import * as tslib_1 from "tslib";
import { ErrorHandler } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AviationEvent } from '../models/aviation-event.model';
import { Photo } from '../models/photo.model';
import { Country } from '../models/country.model';
import { Timezone } from '../models/timezone.model';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "@angular/common";
/**
 * Minimum distance when performing a radius search (nautical miles)
 */
export var MIN_RADIUS = 25;
/**
 * Maximum distance when performing a radius search (nautical miles)
 */
export var MAX_RADIUS = 500;
var AviationEventsService = /** @class */ (function () {
    function AviationEventsService(_http, _decimalPipe, _errorHandler) {
        this._http = _http;
        this._decimalPipe = _decimalPipe;
        this._errorHandler = _errorHandler;
    }
    /**
     * Returns a sanitized search term for airport search, with all invalid characters removed.
     * @param {string} term Original input term
     * @returns {string} Sanitized term
     */
    AviationEventsService.sanitizeSearchTerm = function (term) {
        return (term || '').toString().replace(/[^a-z0-9 ]+/gi, '').trim();
    };
    /**
     * Returns all aviation events
     * @param startDateUTC {string} the start date of the event in UTC in ISO8601 string format
     * @param endDateUTC {string} the end date of the event in UTC in ISO8601 string format
     * @returns {Promise<AviationEvent[]>} List of aviation events
     */
    AviationEventsService.prototype.getAviationEvents = function (startDateUTC, endDateUTC) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events", {
                                startDate: startDateUTC,
                                endDate: endDateUTC
                            }).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AviationEvent.parse)];
                    case 2:
                        e_1 = _a.sent();
                        this._errorHandler.handleError(e_1);
                        throw e_1;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns details of a specific aviation event.
     * @param aviationEventId {number} Event id to return details for
     * @returns {Promise<AviationEvent>} aviation event returned
     */
    AviationEventsService.prototype.getAviationEventById = function (aviationEventId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/events/" + aviationEventId).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, AviationEvent.parse(response)];
                    case 2:
                        e_2 = _a.sent();
                        if (e_2 && e_2.status === 404) {
                            return [2 /*return*/, null];
                        }
                        this._errorHandler.handleError(e_2);
                        throw e_2;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns aviation events for a given search term.
     * @param term {string} Search term
     * @returns {Promise<AviationEvent[]>} List of matching aviation events
     */
    AviationEventsService.prototype.getAviationEventsBySearchTerm = function (term) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_3;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        term = AviationEventsService.sanitizeSearchTerm(term);
                        if (term.length < 3) {
                            throw new Error('At least three alphanumeric characters are necessary to perform a search.');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events", {
                                searchType: 'Basic',
                                searchTerm: "%" + term + "%",
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AviationEvent.parse)];
                    case 3:
                        e_3 = _a.sent();
                        this._errorHandler.handleError(e_3);
                        throw e_3;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns aviation events for a comma separated list of airport IDs.
     * @param airportList {string} Search term
     * @returns {Promise<AviationEvent[]>} List of matching aviation events
     */
    AviationEventsService.prototype.getFavoriteAviationEvents = function (airportList) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_4;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (airportList.length < 3) {
                            throw new Error('At least three alphanumeric characters are necessary to perform a search.');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events", {
                                searchType: 'Favorites',
                                searchTerm: airportList
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AviationEvent.parse)];
                    case 3:
                        e_4 = _a.sent();
                        this._errorHandler.handleError(e_4);
                        throw e_4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Performs a radial search for aviation events within a given distance of a center point.
     * @param latlon {LatLon} Center point of search
     * @param radius {number} Radius in nautical miles
     * @param startDateUTC {string} the start date of the event in UTC in ISO8601 string format
     * @param endDateUTC {string} the end date of the event in UTC in ISO8601 string format
     * @returns {Promise<AviationEvent[]>} List of matching aviation events
     */
    AviationEventsService.prototype.getAviationEventsByLatLon = function (latlon, radius, startDateUTC, endDateUTC) {
        if (startDateUTC === void 0) { startDateUTC = null; }
        if (endDateUTC === void 0) { endDateUTC = null; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var body, response, e_5;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!latlon) {
                            throw new Error('latlon is required');
                        }
                        if (!radius || radius < MIN_RADIUS || radius > MAX_RADIUS) {
                            throw new Error("radius is required and must be between " + MIN_RADIUS + " and " + MAX_RADIUS + " nautical miles.");
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        body = void 0;
                        if (startDateUTC && endDateUTC) {
                            body = {
                                point: {
                                    latitude: latlon.lat,
                                    longitude: latlon.lon
                                },
                                radius: radius,
                                startDate: startDateUTC,
                                endDate: endDateUTC
                            };
                        }
                        else {
                            body = {
                                point: {
                                    latitude: latlon.lat,
                                    longitude: latlon.lon
                                },
                                radius: radius
                            };
                        }
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events", body).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AviationEvent.parse)];
                    case 3:
                        e_5 = _a.sent();
                        this._errorHandler.handleError(e_5);
                        throw e_5;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Performs a bounding box search for aviation events
     * @param bounds {BoundingBox} Bounding box
     * @param startDateUTC {string} the start date of the event in UTC in ISO8601 string format
     * @param endDateUTC {string} the end date of the event in UTC in ISO8601 string format
     * @returns {Promise<AviationEvent[]>} List of matching aviation events
     */
    AviationEventsService.prototype.getAviationEventsByBoundingBox = function (bounds, startDateUTC, endDateUTC) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var area, neLat, neLon, swLat, swLon, response, e_6;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        area = bounds.getAreaDegrees();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        neLat = bounds.ne.lat.toFixed(7);
                        neLon = bounds.ne.lon.toFixed(7);
                        swLat = bounds.sw.lat.toFixed(7);
                        swLon = bounds.sw.lon.toFixed(7);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events", {
                                rectangleLatLong: neLat + "," + neLon + "," + swLat + "," + swLon,
                                startDate: startDateUTC,
                                endDate: endDateUTC
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AviationEvent.parse)];
                    case 3:
                        e_6 = _a.sent();
                        this._errorHandler.handleError(e_6);
                        throw e_6;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns all aviation events within a given state
     * @param state {string} State code
     * @param startDateUTC {string} the start date of the event in UTC in ISO8601 string format
     * @param endDateUTC {string} the end date of the event in UTC in ISO8601 string format
     * @returns {Promise<AviationEvent[]>} List of matching aviation events
     */
    AviationEventsService.prototype.getAviationEventsByState = function (state, startDateUTC, endDateUTC) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_7;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!state) {
                            throw new Error('"state" parameter is required when searching aviation events by state');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events", {
                                stateProvince: state,
                                startDate: startDateUTC,
                                endDate: endDateUTC
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AviationEvent.parse)];
                    case 3:
                        e_7 = _a.sent();
                        this._errorHandler.handleError(e_7);
                        throw e_7;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * adds event
     * @returns {Promise<AviationEvent>} returns event after adding it
     * @param event
     */
    AviationEventsService.prototype.addAviationEvent = function (event) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!event) {
                            throw new Error('"event" parameter is required');
                        }
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/event", event).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, AviationEvent.parse(response)];
                }
            });
        });
    };
    AviationEventsService.prototype.uploadEventCoverImg = function (image, eventId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var contentDisposition, uploadData, e_8;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!image) {
                            throw new Error('"event" parameter is required');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        contentDisposition = 'form-data; name="' + image.name + '"; filename="' + image.name + '"';
                        uploadData = new FormData();
                        // uploadData.set('picture', image);
                        uploadData.set('category', 'event');
                        uploadData.set('eventId', eventId.toString());
                        // uploadData.set(this.selectedFile.name, this.selectedFile.name);
                        uploadData.append('File', image, image.name);
                        uploadData.set(image.name, image.name);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/photos/event/upload", uploadData, {
                                headers: {
                                    'Content-Disposition': contentDisposition
                                }
                            }).toPromise()];
                    case 2: return [2 /*return*/, _a.sent()];
                    case 3:
                        e_8 = _a.sent();
                        console.error(e_8);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    AviationEventsService.prototype.putEventPhoto = function (eventId, photoId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, BODY, e_9;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        response = void 0;
                        BODY = this._getEventPhotoJSON(photoId, eventId);
                        return [4 /*yield*/, this._http.put(environment.airportsApiUrl + "/photos/event/update/" + photoId, BODY).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, Photo.parse(response)];
                    case 2:
                        e_9 = _a.sent();
                        this._errorHandler.handleError(e_9);
                        throw e_9;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    AviationEventsService.prototype._getEventPhotoJSON = function (photoId, eventId) {
        return {
            photoId: photoId,
            entityId: eventId
        };
    };
    AviationEventsService.prototype.deleteEventCoverImg = function (eventId, photoId) {
        var URL = environment.airportsApiUrl + "/photos/event/delete/" + photoId;
        try {
            return this._http.delete(URL).toPromise();
        }
        catch (e) {
            this._errorHandler.handleError(e);
            throw e;
        }
    };
    /**
      * Returns aviation events that match a list of event ids
      * @returns {Promise<AviationEvent[]>} List of aviation events
      */
    AviationEventsService.prototype.getFavoriteEvents = function (eventIds) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, aviationEvents, eventList_1, favoriteEvents_1, e_10;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 4, , 5]);
                        if (!(eventIds.length > 0)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events", {}).toPromise()];
                    case 1:
                        response = _a.sent();
                        aviationEvents = response.map(AviationEvent.parse);
                        eventList_1 = eventIds.split(',');
                        favoriteEvents_1 = [];
                        aviationEvents.forEach(function (ae) {
                            if (eventList_1.includes(ae.eventId.toString())) {
                                favoriteEvents_1.push(ae);
                            }
                        });
                        return [2 /*return*/, favoriteEvents_1];
                    case 2: return [2 /*return*/, null];
                    case 3: return [3 /*break*/, 5];
                    case 4:
                        e_10 = _a.sent();
                        this._errorHandler.handleError(e_10);
                        throw e_10;
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * updates event
     * @returns {Promise<AviationEvent>} returns event after updating it
     * @param event
     */
    AviationEventsService.prototype.updateAviationEvent = function (event) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!event) {
                            throw new Error('"event" parameter is required');
                        }
                        event.interactions = null;
                        return [4 /*yield*/, this._http.put(environment.airportsApiUrl + "/events/" + event.eventId, event).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, AviationEvent.parse(response)];
                }
            });
        });
    };
    AviationEventsService.prototype.deleteAviationEvent = function (eventId) {
        var URL = environment.airportsApiUrl + "/events/" + eventId;
        try {
            return this._http.delete(URL).toPromise();
        }
        catch (e) {
            this._errorHandler.handleError(e);
            throw e;
        }
    };
    /**
     * clones an aviation event in the database using the new dates passed in
     * @returns {Promise<AviationEvent>} returns new cloned event information
     * @param eventId {number} the eventId to create a clone of
     * @param startDateTimeUTC {string} the start date of the event in UTC in ISO8601 string format
     * @param endDateTimeUTC {string} the end date of the event in UTC in ISO8601 string format
     * @param recId {number} the recId of the user attempting to clone the event
     */
    AviationEventsService.prototype.cloneAviationEvent = function (eventId, startDateTimeUTC, endDateTimeUTC, recId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var body, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        body = {
                            'startDateTimeUtc': startDateTimeUTC,
                            'endDateTimeUtc': endDateTimeUTC,
                            'recId': recId
                        };
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/events/" + eventId + "/clone", body).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, AviationEvent.parse(response)];
                }
            });
        });
    };
    /**
     * Returns list of countries
     * @returns {Promise<Country[]>} List of countries
     */
    AviationEventsService.prototype.getCountries = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, countries, e_11;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/events/countries", {}).toPromise()];
                    case 1:
                        response = _a.sent();
                        countries = response.map(Country.parse);
                        if (countries && countries.length > 0) {
                            return [2 /*return*/, countries];
                        }
                        else {
                            return [2 /*return*/, []];
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        e_11 = _a.sent();
                        this._errorHandler.handleError(e_11);
                        throw e_11;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns list of timezones for a country
     * @returns {Promise<Timezone[]>} List of timezones
     * @param countryCode {string} the country to get timezones for
     */
    AviationEventsService.prototype.getTimezones = function (countryCode) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, timezones, e_12;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/events/timezones/" + countryCode, {}).toPromise()];
                    case 1:
                        response = _a.sent();
                        timezones = response.map(Timezone.parse);
                        if (timezones && timezones.length > 0) {
                            return [2 /*return*/, timezones];
                        }
                        else {
                            return [2 /*return*/, []];
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        e_12 = _a.sent();
                        this._errorHandler.handleError(e_12);
                        throw e_12;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    AviationEventsService.ngInjectableDef = i0.defineInjectable({ factory: function AviationEventsService_Factory() { return new AviationEventsService(i0.inject(i1.HttpClient), i0.inject(i2.DecimalPipe), i0.inject(i0.ErrorHandler)); }, token: AviationEventsService, providedIn: "root" });
    return AviationEventsService;
}());
export { AviationEventsService };
